import React, { useCallback, useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector, shallowEqual } from 'react-redux';
import { useParams } from 'react-router-dom';
import { makeStyles } from '@material-ui/core/styles';
import { getAuth, viewHeightWithoutAppbar } from 'util/index';
import { selectedFspIdSelector } from 'selectors/fsp';
import {
  load as guestLoanSearchLoad,
  loadHistory,
  loadScheduled,
} from 'model/guestLoanSearch';
import {
  guestLoanSelector,
  selectGuestLoanHistoryTransactions,
  selectGuestLoanScheduledTransactions,
} from 'selectors/guestLoanSearch';
import { useScrollState } from 'hooks';
import PayerDetailSectionsProgress from './PayerDetailSectionsProgress';
import GuestTransactionHistoryPanel from './Transactions/GuestTransactionHistoryPanel';
import GuestScheduledTransactionsPanel from './Transactions/GuestScheduledTransactionsPanel';
import GuestProfile from './Profile/GuestProfile';

const useStyles = (addHeight) => {
  return makeStyles((theme) => ({
    container: {
      paddingLeft: theme.spacing(1),
      paddingRight: theme.spacing(1),
      paddingTop: theme.spacing(0.5),
    },
    root: {
      contentVisibility: 'auto',
      minHeight: theme.breakpoints.values.sm / 2,
      overflowY: 'auto',
      ...(addHeight ? viewHeightWithoutAppbar(theme) : null),
    },
  }))();
};

const GuestPayerDetailSections = ({ addHeight }) => {
  const classes = useStyles(addHeight);
  const dispatch = useDispatch();
  const ref = useRef();
  const { number, type } = useParams();
  const fspId = useSelector(selectedFspIdSelector);
  const auth = useSelector(getAuth);
  const guestLoans = useSelector(guestLoanSelector, shallowEqual);

  const storedLoanNumber = localStorage.getItem('guestLoanNumber');
  const storedLoanType = localStorage.getItem('guestLoanType');
  const loanNumberToShow = storedLoanNumber || number;
  const loanTypeToShow = storedLoanType || type || 'guest';

  const guestLoan = guestLoans.find(
    (loan) => String(loan.number) === String(loanNumberToShow)
  );

  const lastLoadedLoanRef = useRef();
  const lastLoadedHistoryRef = useRef();
  const lastLoadedScheduledRef = useRef();

  useEffect(() => {
    lastLoadedLoanRef.current = undefined;
    lastLoadedHistoryRef.current = undefined;
    lastLoadedScheduledRef.current = undefined;
  }, [loanNumberToShow, fspId, loanTypeToShow, auth]);

  useEffect(() => {
    if (
      fspId &&
      loanNumberToShow &&
      loanTypeToShow &&
      auth &&
      !guestLoan &&
      lastLoadedLoanRef.current !== loanNumberToShow
    ) {
      dispatch(
        guestLoanSearchLoad(
          { fspId, loanNumber: loanNumberToShow, loanType: loanTypeToShow },
          auth
        )
      );
      lastLoadedLoanRef.current = loanNumberToShow;
    }
  }, [dispatch, fspId, loanNumberToShow, loanTypeToShow, auth, guestLoan]);

  const handleHistoryOpen = useCallback(() => {
    if (
      fspId &&
      loanNumberToShow &&
      auth &&
      guestLoan &&
      lastLoadedHistoryRef.current !== loanNumberToShow
    ) {
      dispatch(loadHistory({ fspId, loanNumber: loanNumberToShow, auth }));
      lastLoadedHistoryRef.current = loanNumberToShow;
    }
  }, [dispatch, fspId, loanNumberToShow, auth, guestLoan]);

  const handleScheduledOpen = useCallback(() => {
    if (
      fspId &&
      loanNumberToShow &&
      auth &&
      guestLoan &&
      lastLoadedScheduledRef.current !== loanNumberToShow
    ) {
      dispatch(loadScheduled({ fspId, loanNumber: loanNumberToShow, auth }));
      lastLoadedScheduledRef.current = loanNumberToShow;
    }
  }, [dispatch, fspId, loanNumberToShow, auth, guestLoan]);

  useEffect(() => {
    if (guestLoan) {
      if (localStorage.getItem('guestLoanNumber') !== guestLoan.number) {
        localStorage.setItem('guestLoanNumber', guestLoan.number);
      }
      if (localStorage.getItem('guestLoanType') !== guestLoan.type) {
        localStorage.setItem('guestLoanType', guestLoan.type);
      }
    }
  }, [guestLoan]);

  const scheduledTransactionsFromRedux = useSelector(
    (state) => selectGuestLoanScheduledTransactions(state, loanNumberToShow),
    shallowEqual
  );
  const historyTransactions = useSelector(
    (state) => selectGuestLoanHistoryTransactions(state, loanNumberToShow),
    shallowEqual
  );

  const [scheduledTransactions, setScheduledTransactions] = useState(
    scheduledTransactionsFromRedux
  );

  useEffect(() => {
    setScheduledTransactions(scheduledTransactionsFromRedux);
  }, [scheduledTransactionsFromRedux]);

  const handleScheduledCancelSuccess = (cancelledPayment) => {
    setScheduledTransactions((prev) =>
      prev.filter((item) => String(item.id) !== String(cancelledPayment.id))
    );
  };

  const handleHistoryUpdateSuccess = useCallback(() => {
    if (fspId && loanNumberToShow && auth) {
      lastLoadedHistoryRef.current = undefined;
      dispatch(loadHistory({ fspId, loanNumber: loanNumberToShow, auth }));
    }
  }, [dispatch, fspId, loanNumberToShow, auth]);

  useScrollState('pos', ref.current);

  return (
    <div ref={ref} className={classes.root}>
      <PayerDetailSectionsProgress />
      <div className={classes.container}>
        <GuestProfile loanNumber={loanNumberToShow} loanType={loanTypeToShow} />
        <GuestScheduledTransactionsPanel
          items={scheduledTransactions}
          loanNumber={loanNumberToShow}
          fspId={fspId}
          onCancelSuccess={handleScheduledCancelSuccess}
          onOpen={handleScheduledOpen}
        />
        <GuestTransactionHistoryPanel
          items={historyTransactions}
          loanNumber={loanNumberToShow}
          fspId={fspId}
          onOpen={handleHistoryOpen}
          onUpdateSuccess={handleHistoryUpdateSuccess}
        />
      </div>
    </div>
  );
};

GuestPayerDetailSections.propTypes = {
  addHeight: PropTypes.bool,
};

GuestPayerDetailSections.defaultProps = {
  addHeight: true,
};

export default GuestPayerDetailSections;
